/**
 * zod schemas for the Azure Storage connection fields that arrive from outside
 * the type system — the connection form (`connectionFormSchema.ts`) and the MCP
 * `connections_add` tool (`electron/mcpServer.ts`). Kept beside the types so a
 * change to `StorageResourceRef` is a change here too (the `satisfies` pins it).
 */
import { z } from 'zod';
import type { AzureStorageEndpoints, StorageAttachmentScope, StorageResourceRef, StorageService } from './azureStorage';

export const storageServiceSchema = z.enum(['blob', 'table', 'queue', 'file']) satisfies z.ZodType<StorageService>;

export const azureStorageEndpointsSchema = z.object({
  blob: z.string().optional(),
  table: z.string().optional(),
  queue: z.string().optional(),
  file: z.string().optional(),
  dfs: z.string().optional(),
}) satisfies z.ZodType<AzureStorageEndpoints>;

export const storageResourceRefSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('account') }),
  z.object({ kind: z.literal('service'), service: storageServiceSchema }),
  z.object({ kind: z.literal('container'), container: z.string() }),
  z.object({ kind: z.literal('blobPrefix'), container: z.string(), prefix: z.string() }),
  z.object({ kind: z.literal('blob'), container: z.string(), name: z.string(), snapshot: z.string().optional(), versionId: z.string().optional() }),
  z.object({ kind: z.literal('table'), table: z.string() }),
  z.object({ kind: z.literal('entity'), table: z.string(), partitionKey: z.string(), rowKey: z.string() }),
  z.object({ kind: z.literal('queue'), queue: z.string() }),
  z.object({ kind: z.literal('message'), queue: z.string(), messageId: z.string(), popReceipt: z.string().optional() }),
  z.object({ kind: z.literal('share'), share: z.string(), snapshot: z.string().optional() }),
  z.object({ kind: z.literal('directory'), share: z.string(), path: z.string(), snapshot: z.string().optional() }),
  z.object({ kind: z.literal('file'), share: z.string(), path: z.string(), snapshot: z.string().optional() }),
  z.object({ kind: z.literal('hnsPath'), fileSystem: z.string(), path: z.string(), isDirectory: z.boolean() }),
]) satisfies z.ZodType<StorageResourceRef>;

export const storageAttachmentScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('account') }),
  z.object({ kind: z.literal('resource'), resource: storageResourceRefSchema }),
]) satisfies z.ZodType<StorageAttachmentScope>;
